import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/atoms/button";
import { type DocumentVersionSummary } from "~/api/types";
import { Utils } from "~/api/utils";
import { ProgressBar } from "~/atoms/progress-bar";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";

interface DownloadDatabaseModalProps {
  trigger: ReactNode;
  documentVersions: DocumentVersionSummary[];
}

export const DownloadDatabaseModal = ({
  trigger,
  documentVersions,
}: DownloadDatabaseModalProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("commons.download.documents")}</DialogTitle>
          <DialogDescription>
            {t("commons.download.description")}
          </DialogDescription>
        </DialogHeader>
        <DownloadDatabase
          documentVersions={documentVersions}
          onClose={() => setIsOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

interface Props {
  documentVersions: DocumentVersionSummary[];
  onClose: () => void;
}

const DownloadDatabase = ({ documentVersions, onClose }: Props) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [progress, setProgress] = useState(0);

  const updateProgress = useCallback((value: number) => setProgress(value), []);

  const downloadDatabase = async () => {
    if (documentVersions.length === 0) {
      toast.info(t("toasts.info.noDocumentsFound"));
      return;
    }

    try {
      setError(false);
      setIsDownloading(true);
      await Utils.saveZipDocuments(documentVersions, updateProgress);
      onClose();
    } catch {
      setError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isDownloading) {
    return (
      <div className="grid gap-6">
        <ProgressBar
          value={(progress / Math.max(documentVersions.length, 1)) * 100}
        />
        <div>
          {t("commons.download.downloading")} {progress} /{" "}
          {documentVersions.length}
        </div>
        {error && (
          <div className="text-red-600">{t("commons.download.error")}</div>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <DialogDescription>
        {t("library.filters.selectedCount", {
          count: documentVersions.length,
        })}
      </DialogDescription>
      {error && (
        <div className="text-red-600">{t("commons.download.error")}</div>
      )}

      <DialogFooter>
        <Button onClick={downloadDatabase} disabled={isDownloading}>
          {t("commons.buttons.download")}
        </Button>
      </DialogFooter>
    </div>
  );
};
